/**
 * Cookie-Consent-Logik (DSGVO + Google Consent Mode v2).
 * Vor der Einwilligung wird nichts geladen; erst nach Zustimmung werden
 * GTM/GA nachgeladen und die Einwilligung an Google signalisiert.
 */

export const CONSENT_STORAGE_KEY = "wedev-consent-v1";
export const CONSENT_EVENT = "wedev:open-cookie-settings";
/** Wird gefeuert, sobald eine Auswahl gespeichert wurde. */
export const CONSENT_CHANGED = "wedev:consent-changed";

export type ConsentState = {
  statistics: boolean;
  marketing: boolean;
  ts: number;
};

export const DENIED_CONSENT: Omit<ConsentState, "ts"> = {
  statistics: false,
  marketing: false,
};

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (typeof parsed.statistics !== "boolean" || typeof parsed.marketing !== "boolean") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(c: Omit<ConsentState, "ts">): ConsentState {
  const full: ConsentState = { ...c, ts: Date.now() };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full));
  } catch {
    /* Speicher nicht verfügbar – Consent gilt dann nur für die Sitzung. */
  }
  // Signal für alles, was auf die Einwilligung wartet – etwa die
  // Klick-ID, die erst jetzt ins Cookie darf. Bewusst ein Ereignis statt
  // eines direkten Aufrufs: lib/klick-id.ts liest hier Consent, ein
  // direkter Aufruf ergäbe einen Ringschluss zwischen beiden Dateien.
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED));
  } catch {
    /* Kein Fenster (Server) – dann gibt es auch niemanden, der zuhört. */
  }
  return full;
}

/* ---- Google Consent Mode v2 ---- */

type GtagConsentValue = "granted" | "denied";

/**
 * gtag()-Aufruf – exakt wie in Googles Snippet.
 *
 * WICHTIG: In den dataLayer muss das `arguments`-Objekt, kein Array.
 * gtag.js und Google Tag Manager erkennen Befehle wie "consent" nur in
 * dieser Form; ein Array ignorieren sie stillschweigend. Bis 2026-09-11
 * stand hier `push(args)` – Consent Mode v2 kam dadurch nie bei Google an:
 * leerer Consent-Zustand in gtag.js, kein `gcs`-Parameter in den Treffern.
 * Für Google Ads im EWR heißt das: Conversions ohne Einwilligungssignal.
 */
export function gtag(...args: unknown[]): void;
export function gtag(): void {
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

/** Standard: alles verweigert – muss vor dem Laden von GTM/GA gesetzt sein. */
export function setDefaultConsent() {
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

/** Aktualisiert den Consent-Status entsprechend der Nutzerauswahl. */
export function updateConsent(c: Omit<ConsentState, "ts">) {
  const v = (b: boolean): GtagConsentValue => (b ? "granted" : "denied");
  gtag("consent", "update", {
    analytics_storage: v(c.statistics),
    ad_storage: v(c.marketing),
    ad_user_data: v(c.marketing),
    ad_personalization: v(c.marketing),
  });
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(CONSENT_EVENT));
}
